/**
 * The `sync:rejected` handler: turns an engine rejection into something a
 * human actually sees.
 *
 * Split out of useSyncEngine.js on purpose. The hook is a subscription wiring
 * point mounted once from App.js and can only be exercised through
 * renderHook + a mocked tauri event bus; the decision this module makes —
 * "does this rejection reach the user, and with what words" — is the part that
 * was broken and the part that must stay directly testable.
 *
 * Surface choice: popup toasts are globally disabled in this app
 * (components/ui/Toast.js sets POPUP_TOASTS_ENABLED = false and addToast
 * early-returns), so showToast here would be another silent drop — the exact
 * failure mode being fixed. The live surfaces are useNotificationStore
 * (rendered by NotificationContainer via ModalStack) and durable markers, and
 * this uses both.
 */
import { useNotificationStore } from '../store/notificationStore';
import { useSyncRejectionStore } from '../store/syncRejectionStore';
import { quotaMessage } from './planQuota';
import { notePlanLimitRefusal } from '../store/planLimitStore';
import { useVaultCapStore } from './vaultCloudCap';
import { addSessionEndedListener } from './channelService';
import { cloudPlanRefusal } from '../plans/cloudState';
import i18n from '../i18n';

/**
 * The one sentence of client-authored copy. It says nothing about WHICH limit
 * was hit or what the plan costs — that is the server's message, appended
 * verbatim below — only what the client alone knows: the row exists here and
 * nowhere else. That is the fact whose absence made this look like data loss
 * on the user's second machine.
 */
export const LOCAL_ONLY_SUFFIX = 'This change is saved on this device only.';

const TITLE = 'Not synced';

/** Scopes whose cloud plan refusal was already announced this session. */
const announcedPlanRefusals = new Set();
addSessionEndedListener(() => announcedPlanRefusals.clear());

/** Human-ish label for the notification title, per entity kind. */
const ENTITY_LABEL = {
  collections: 'Collection',
  requests: 'Request',
  environments: 'Environment',
  variable_scopes: 'Variables',
  schema_nodes: 'Item',
  extension_documents: 'Document',
  vault_records: 'Vault item',
};

/** Card U1: the free cloud vault's cap refused a new vault item. */
const isVaultCap = (entry) => entry?.entityType === 'vault_records' && String(entry?.error || '').includes('upgrade_required');

export function rejectionMessage(entry, { web = false } = {}) {
  if (isVaultCap(entry)) {
    return 'Your free cloud vault is full. This item is saved on this device only — upgrade for unlimited items, or keep it here.';
  }
  // Free-plan sync limits get their own words (services/planQuota): the
  // server sends a machine code there, not a sentence.
  const quota = quotaMessage(entry?.error, { web });
  if (quota) return quota;
  const server = String(entry?.error || '').trim();
  return server ? `${server} ${LOCAL_ONLY_SUFFIX}` : LOCAL_ONLY_SUFFIX;
}

/**
 * Handles one `sync:rejected` payload
 * ({ workspaceId, rejected: [{ entityId, entityType, error }] }).
 *
 * Returns the entries it announced, so callers (and tests) can tell an
 * announcement from a de-duplicated repeat.
 */
export function handleSyncRejected(payload) {
  const fresh = useSyncRejectionStore.getState().record(payload);
  // A plan limit also shows in the titlebar sync menu (with the usage bars).
  (payload?.rejected || []).forEach((entry) => notePlanLimitRefusal(entry?.error));

  // Card U1: the cap card for the refused vault items (the race — the
  // editor asks BEFORE saving when it already knows the vault is full).
  const capped = fresh.filter(isVaultCap).map((e) => e.entityId);
  if (capped.length && !payload?.web) useVaultCapStore.getState().show(capped);

  // Plans v2: a cloud plan refusal (402 read-only / no cloud) parks a whole
  // batch at once — say it ONCE per scope and session, not once per op.
  const planRefused = fresh.filter((e) => !isVaultCap(e) && cloudPlanRefusal(e?.error));
  if (planRefused.length) {
    const key = String(payload?.workspaceId ?? '');
    if (!announcedPlanRefusals.has(key)) {
      announcedPlanRefusals.add(key);
      const refusal = cloudPlanRefusal(planRefused[0].error);
      useNotificationStore.getState().addNotification({
        type: 'warning',
        title: TITLE,
        // A per-op "read_only" carries no sentence: the app's own words then.
        message: `${refusal.error || i18n.t('cloudLocal.readOnlyRefused')} ${LOCAL_ONLY_SUFFIX}`,
        duration: 0,
      });
    }
  }

  fresh.forEach((entry) => {
    // The cap card is the surface for those — not one notice per item.
    if (!payload?.web && isVaultCap(entry)) return;
    if (planRefused.includes(entry)) return;
    const label = ENTITY_LABEL[entry.entityType];
    useNotificationStore.getState().addNotification({
      type: 'warning',
      title: label ? `${TITLE}: ${label}` : TITLE,
      message: rejectionMessage(entry, { web: !!payload?.web }),
      // duration 0 disables notificationStore's auto-dismiss timer. A
      // rejection has no retry and no repair path in the engine — telling the
      // user IS the repair — so it must not evaporate after five seconds
      // while they are looking somewhere else. It still has a close button,
      // and the durable marker in syncRejectionStore outlives the dismissal.
      duration: 0,
    });
  });

  return fresh;
}

export default handleSyncRejected;
